/* Extendables */
export type SelectorItem = {
  id: string | number;
  name: string;
  displayName?: string;
};

/* Shared */
export enum FormStatus {
  PENDING = 0,
  OK = 1
}

export enum CustomerSource {
  WEBSITE = 'WEBSITE',
  EMAIL = 'EMAIL',
  WHATSAPP = 'WHATSAPP',
  REFERRAL = 'REFERRAL',
  OTHER = 'OTHER'
}

// REQ-31 (apps/api's CustomerLanguage, docs/class.puml): the language a customer is written to,
// set by staff. Not a closed list -- adding a language is one line here and one in
// CUSTOMER_LANGUAGE_LABELS (utils/customer-language.ts), whose Record type makes the compiler ask
// for it. Every screen builds its options from CUSTOMER_LANGUAGES, never from a branch per language.
export enum CustomerLanguage {
  ES = 'ES',
  EN = 'EN',
  NL = 'NL'
}

/* ********* */
/* CUSTOMERS */
/* ********* */
/* Value Objects */
type Phone = {
  country: string;
  number: string;
};
export type Country = {
  id: number;
  name: string;
  displayName: string;
  phone: string;
  alpha3: string;
};
type Address = {
  street: string;
  city: string;
  postalCode: string;
};
type MapMarker = {
  latitude: number;
  longitude: number;
};

/* Requests */
export type RegisterCustomerRequest = {
  firstName: string;
  lastName?: string | undefined;
  phoneCountry: string;
  phoneNumber: string;
  email?: string | undefined;
  notes?: string | undefined;
  source?: CustomerSource | undefined;
  sourceDetails?: string | undefined;
  loyverseId?: string | null | undefined;
  googleContactResourceName?: string | null | undefined;
  // REQ-31: required here although the API treats an omitted one as none (apps/api's
  // RegisterCustomerRequest), so no caller leaves it to chance: a new customer carries the default
  // language, and an update carries the customer's own (null for one with none) -- the PUT replaces
  // it like every other field, so a save that left it out would clear the customer's language.
  language: CustomerLanguage | null;
  // REQ-12: required here for the same reason as `language` above -- apps/api's
  // RegisterCustomerRequest/UpdateCustomerRequest both treat a missing value as off, and the PUT
  // replaces it like every other field, so a save that left it out would silently opt the customer
  // back out. Off for a new customer (owner decision 2026-11-01); an update carries the customer's
  // own current value.
  whatsappNotifications: boolean;
};
// REQ-4/REQ-9: a PUT must carry the version the screen read (apps/api/README.md § API
// Conventions) -- a stale value answers 409 (see src/utils/utils.ts's isConflictError).
// REQ-7: `defaultWaterBottleItemId` is required here (not optional) so every caller must round-trip
// the current value explicitly -- apps/api's UpdateCustomerRequest treats it like every other
// optional field on this DTO, unconditionally overwritten on every save (SHARED_CONTRACTS), so a
// caller that forgot it would silently null it out. Not on RegisterCustomerRequest: a new customer
// has no orders or subscriptions yet to default a bottle from (docs/class.puml's own note on
// Customer).
// REQ-29: `conversationSummary` is required here for the same reason (apps/api's
// UpdateCustomerRequest): the PUT replaces it, and a request without one (or a blank one) clears
// it, so every caller must carry the customer's current text. The agent writes it too, so a save
// that did not mean to change it sends the text the customer holds now, never a stale copy (see
// utils/conversation-summary.ts). Not on RegisterCustomerRequest: the API ignores it on create and
// a new customer has none.
export type UpdateCustomerRequest = RegisterCustomerRequest & {
  version: number;
  defaultWaterBottleItemId: string | null;
  conversationSummary: string | null;
};

// POST /api/v1/customers -- 201, body `{id}` only (apps/api/README.md § API Conventions): the
// full customer is never echoed back, the UI re-GETs it (REQ-2).
export type CreateCustomerResponse = { id: string };

export type UpdateInvoiceAccountRequest = {
  id?: string | undefined;
  companyName: string;
  sageCode?: string | undefined;
  street: string;
  city: string;
  postalCode: string;
  // REQ-4: the *customer's* version, not the invoice account's own id above (apps/api/README.md
  // § API Conventions).
  version: number;
};

export type UpdateDeliveryAddressRequest = {
  id?: string | undefined;
  street: string;
  city: string;
  postalCode: string;
  latitude?: number | undefined;
  longitude?: number | undefined;
  // REQ-4: the customer's version (apps/api/README.md § API Conventions).
  version: number;
  // REQ-8: staff-only free text, nullable -- never given to the agent (apps/api's
  // UpdateDeliveryAddressRequest.java). Appended last, matching the backend's own field order.
  notes?: string | null | undefined;
};

/* Entities */
export type Customer = {
  id: string;
  firstName: string;
  lastName: string | null;
  phone: Phone;
  email: string | null;
  notes: string | null;
  source: CustomerSource | null;
  sourceDetails: string | null;
  // REQ-7: a Loyverse item id, nullable, no FK -- filled once by apps/api's backfill, then set and
  // changed by staff only; the order line the agent uses for "water" (docs/class.puml).
  defaultWaterBottleItemId: string | null;
  // REQ-12: off for every customer until staff turn it on; a STOP reply to the agent turns it back
  // off on apps/api's side (docs/class.puml).
  whatsappNotifications: boolean;
  // REQ-31: null for a customer staff have not given a language yet, which the API treats as
  // Spanish (docs/class.puml) and lists in the Data cleansing check "Customers with no language".
  language: CustomerLanguage | null;
  // REQ-29 (docs/class.puml): how the customer's earlier agent conversations ended, one line per
  // conversation separated by `\n`, oldest first, at most 1,000 characters. null when there is none,
  // never an empty string. The agent adds lines; staff edit the whole text.
  conversationSummary: string | null;
  invoiceAccount: InvoiceAccount | null;
  deliveryAddresses: DeliveryAddress[];
  // REQ-5: flattened across every delivery address, not nested under deliveryAddresses[] any more
  // -- group by `deliveryAddressId` to show a given address's own subscriptions (see
  // src/components/Customer/delivery-addresses.tsx).
  subscriptions: Subscription[];
  // REQ-4/REQ-9: the whole-aggregate optimistic-locking version -- an update to the customer, its
  // invoice account or one of its delivery addresses carries this back (apps/api/README.md § API
  // Conventions).
  version: number;
  createdAt: string;
};

export type InvoiceAccount = {
  id: string;
  companyName: string;
  sageCode: string;
  address: InvoiceAddress;
};

type InvoiceAddress = Address & {
  id: string;
};

// REQ-5: the one delivery address shape shared by the customer response, the delivery-group
// endpoints (DeliveryGroupAddress below) and the unassigned-addresses endpoint
// (UnassignedDeliveryAddress below) -- apps/api/README.md § API Conventions. It carries no
// subscription data any more: a customer's own subscriptions are read from Customer.subscriptions
// above and grouped back to their address by `deliveryAddressId`.
export type DeliveryAddress = Address & {
  id: string;
  mapMarker: MapMarker | null;
  customerId: string;
  // REQ-2: the customer's first and last name only (apps/api's Customer#getDisplayName) -- never
  // invoice-account-aware the way src/utils/utils.ts's formatCustomerName is, because this shape
  // carries no invoiceAccount to read one from.
  customerName: string;
  deliveryGroupId: string | null;
  deliveryPriority: number | null;
  // REQ-8: staff-only free text, nullable -- never given to the agent, so staff keep notes out of
  // street and city instead (apps/api's DeliveryAddressResponse.java).
  notes: string | null;
};

// The same shape as DeliveryAddress above -- kept as its own name so a caller reading "the
// unassigned list" or "a group's addresses" doesn't import a type literally called
// DeliveryAddress, even though all three endpoints return the one shared DeliveryAddressResponse.
export type UnassignedDeliveryAddress = DeliveryAddress;
export type DeliveryGroupAddress = DeliveryAddress;

/* ************ */
/* SUBSCRIPTION */
/* ************ */

// `SHORT_RENTAL`, not `SHORT_TERM_RENTAL`: this hand-written union must match apps/api's
// SubscriptionType enum field for field (apps/ui/AGENTS.md § API types are hand-written) --
// REQ-9/Jira H2O-23 fixed this name, which disagreed with the API before this plan.
export type SubscriptionType = 'E_MAX' | 'ELEGANCE' | 'WATER_ONLY' | 'SHORT_RENTAL';

export type Subscription = {
  id: string;
  startDate: string;
  endDate: string;
  price: number;
  type: SubscriptionType;
  active: boolean;
  // REQ-26/REQ-27: computed at read time by apps/api (apps/api/README.md § Subscription
  // Management) -- ended, or ending within 30 days of today (Europe/Madrid). Never recomputed
  // here; this app only displays it (src/components/Forms/order-renewal-warning.tsx).
  renewalDue: boolean;
  // REQ-5: which of the customer's delivery addresses this subscription belongs to -- the
  // customer-level SubscriptionResponse's replacement for the old deliveryAddresses[].subscriptions
  // nesting (apps/api/README.md § API Conventions).
  deliveryAddressId: string;
  // REQ-4/REQ-9: this subscription's own optimistic-lock value, distinct from the customer's own
  // `version` on the Customer type above -- the one the client must send back on
  // `PUT /api/v1/subscriptions/{id}/end-date` (apps/api/README.md § API Conventions).
  version: number;
};

// GET /api/v1/subscriptions and /api/v1/subscriptions/upcoming-actions share this row shape
// (apps/api/README.md § API Conventions) -- REQ-8.
export type SubscriptionListRow = {
  id: string;
  type: SubscriptionType;
  startDate: string;
  endDate: string;
  price: number;
  active: boolean;
  customerId: string;
  // First + last name only, like DeliveryAddress.customerName above.
  customerName: string;
  street: string;
  city: string;
  // REQ-4/REQ-9: this subscription's own optimistic-lock value -- the one the client reads here
  // (or off the customer response's nested Subscription) and must send back on
  // `PUT /api/v1/subscriptions/{id}/end-date` (apps/api/README.md § API Conventions).
  version: number;
};

export type SubscriptionStatusFilter = 'ACTIVE' | 'ENDING_SOON' | 'EXPIRED' | 'ALL';

// REQ-9/REQ-10: the administrator's only subscription write (apps/api SetSubscriptionEndDateRequest)
// -- any date, past or future, with a required reason, audited server-side. `endDate` is a plain
// calendar day, "yyyy-MM-dd", no time or zone (apps/api SubscriptionApi#setEndDate's own doc
// comment): sending a serialized instant instead round-trips through two zones that do not agree
// on the resulting day. `version` is this subscription's own optimistic-lock value, read off
// `Subscription.version` (customer-nested) or `SubscriptionListRow.version` -- a stale one answers
// 409 (REQ-4/REQ-9, apps/api/README.md § API Conventions).
export type SetSubscriptionEndDateRequest = {
  endDate: string;
  reason: string;
  version: number;
};

/* ******** */
/* LOYVERSE */
/* ******** */

export type LoyverseItem = {
  id: string;
  itemName: string;
  categoryId: string;
  categoryName: string;
};

export type LoyverseCustomer = {
  id: string;
  name: string;
  email?: string;
};

/* ******** */
/* GOOGLE   */
/* ******** */

export type GoogleContact = {
  resourceName: string;
  name: string;
  phoneNumbers: string[];
};

/* ******** */
/* ORDERS   */
/* ******** */

// REQ-2/REQ-5: `customerId`/`customerName` are flat fields now, not a nested Customer -- and
// `customerName` is the plain first+last display name (apps/api's Customer#getDisplayName), never
// invoice-account-aware (see DeliveryAddress.customerName's own comment above).
// REQ-9: `agentCreated`/`approvedBy`/`approvedAt` are on every order now, not just the review
// queue's own list (docs/order-state.puml REVIEW_MARK) -- `approvedAt` null while `agentCreated` is
// true is the "awaiting review" mark; only Order#approve (the Agent Orders page) ever sets it.
// REQ-44: `payLater`, the derived `amountOwed`/`paymentStatus` and the `closed*` fields are M15's
// (docs/class.puml's Order note) -- all null/false for an order that was never marked "delivered,
// pay later" (every order completed by a Loyverse receipt, and every order delivered before M15).
export type Order = {
  id: string;
  customerId: string;
  customerName: string;
  deliveryAddress: DeliveryAddress;
  status: OrderStatus;
  orderItems: OrderItem[];
  orderDate: string;
  deliveryDate?: string;
  total: number;
  loyverseReceiptNumber: string | null;
  agentCreated: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
  // REQ-36: staff text the driver reads on the printed route sheet, null when the order has none.
  note: string | null;
  // REQ-38: "deliver on this day anyway" -- lifts only the weekday rule (ExcludedOrderReason
  // NOT_A_DELIVERY_DAY), never the cutoff, coordinates, bottle value or review-mark rules.
  deliverAnyway: boolean;
  // REQ-41/REQ-44: true once staff mark the order delivered, pay later; never cleared by a
  // payment -- it says how the order is paid, not whether it still owes.
  payLater: boolean;
  // REQ-40/REQ-44: total minus the sum of its payment allocations, null unless `payLater`. A
  // closed order keeps its real figure here (docs/order-payment-state.puml).
  amountOwed: number | null;
  paymentStatus: OrderPaymentStatus | null;
  // REQ-43: staff closed a pay-later order that still owed an amount -- all three set together,
  // all three null while not closed.
  closedBy: string | null;
  closedAt: string | null;
  closeReason: string | null;
  // REQ-4/REQ-9: the whole-aggregate version, order items included -- a staff update carries this
  // back; cancel carries none (apps/api/README.md § API Conventions).
  version: number;
};

export enum OrderStatus {
  PENDING = 'PENDING',
  READY_FOR_DELIVERY = 'READY_FOR_DELIVERY',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED'
}

// REQ-40/REQ-43 (apps/api's OrderPaymentStatus, docs/order-payment-state.puml): derived, never
// stored -- CLOSED while `Order.closedAt` is set, otherwise from the amount owed. Only present on
// an order with `payLater` true.
export enum OrderPaymentStatus {
  UNPAID = 'UNPAID',
  PARTLY_PAID = 'PARTLY_PAID',
  PAID = 'PAID',
  CLOSED = 'CLOSED'
}

// REQ-41: staff mark a planned order "delivered, pay later" -- `deliveredOn` is the calendar day
// it was delivered ("yyyy-MM-dd"), omitted keeps the order's own planned delivery date.
export type DeliverPayLaterRequest = {
  deliveredOn?: string | undefined;
  version: number;
};

// REQ-43: staff close an order that still owes an amount, with a required reason (apps/api's
// CloseOrderRequest, at most 500 characters).
export type CloseOrderRequest = {
  reason: string;
  version: number;
};

// REQ-43: staff reopen a closed order so it owes and takes payments again.
export type ReopenOrderRequest = {
  version: number;
};

// REQ-44/REQ-45: `GET /orders/owing` -- one unpaid or partly paid order on the Payments page's
// unpaid list, or (REQ-44) in the record-payment dialog's split for one customer (apps/api's
// OwingCustomerResponse.OwingOrder). `paymentStatus` is always UNPAID or PARTLY_PAID here: the
// endpoint excludes paid and closed orders. `ageInDays`: whole days since the delivery day
// (Europe/Madrid), 0 on that day.
export type OwingOrder = {
  id: string;
  deliveryDate: string;
  total: number;
  amountOwed: number;
  paymentStatus: OrderPaymentStatus;
  ageInDays: number;
  version: number;
};

// REQ-44/REQ-45: one customer on the unpaid list, with every one of their own owing orders,
// oldest delivery first (apps/api's OwingCustomerResponse).
export type OwingCustomer = {
  customerId: string;
  customerName: string;
  amountOwed: number;
  orders: OwingOrder[];
};

// REQ-45: one order on the Payments page's "amount differs" review list (apps/api's
// AmountDifferenceResponse) -- `differingReceiptTotal` is the final receipt's total that did not
// match what the order owed at that moment (REQ-42); `paymentStatus` here can be any of the four
// values, unlike OwingOrder above, since a reviewed-but-still-flagged order can be PAID or CLOSED.
export type AmountDifference = {
  id: string;
  customerId: string;
  customerName: string;
  deliveryDate: string;
  total: number;
  differingReceiptTotal: number;
  amountOwed: number;
  paymentStatus: OrderPaymentStatus;
  version: number;
};

// REQ-45: staff mark an "amount differs" order reviewed, with the version they read -- it leaves
// the review list; the status and the stored `differingReceiptTotal` are otherwise untouched
// (docs/order-payment-state.puml AD2).
export type ReviewAmountDifferenceRequest = {
  version: number;
};

// REQ-9: `itemId` is null exactly when `genericWater` is true -- the agent's "water" line on a
// customer with no default water bottle, left for staff to resolve in review by resending this
// line with a real Loyverse item (docs/class.puml).
export type OrderItem = {
  id: string;
  itemId: string | null;
  itemName: string;
  quantity: number;
  price: number;
  total: number;
  genericWater: boolean;
};

/* Requests */
// REQ-36/REQ-38: `note` is at most 500 characters, refused past that; absent or blank is stored
// as none, never sent to the agent's model, Chatwoot, Loyverse or Google Contacts.
// `deliverAnyway` ("deliver on this day anyway") is false when absent.
export type CreateOrderRequest = {
  customerId: string;
  deliveryAddressId: string;
  orderItems: OrderItemRequest[];
  deliveryDate?: string | undefined;
  note?: string | undefined;
  deliverAnyway?: boolean | undefined;
};

export type OrderItemRequest = {
  id: string;
  name: string;
  quantity: number;
};

// REQ-4/REQ-9: carries the version the screen read; a stale value answers 409
// (apps/api/README.md § API Conventions). `deliveryAddressId` (REQ-9) is optional -- omitted or
// null leaves the address unchanged; sent, it must be one of the order's own customer's addresses
// (apps/api answers 400 otherwise).
// REQ-36: `note` is always replaced -- omitting it, or sending an empty one, clears it, so every
// caller resends the order's own current note. REQ-38: `deliverAnyway` follows the usual
// null-leaves-it-unchanged shape instead.
export type UpdateOrderRequest = {
  status?: OrderStatus | undefined;
  deliveryDate?: string | undefined;
  orderItems?: OrderItemRequest[] | undefined;
  deliveryAddressId?: string | null | undefined;
  note?: string | undefined;
  deliverAnyway?: boolean | null | undefined;
  version: number;
};

/* **************** */
/* DELIVERY GROUPS  */
/* **************** */

// Monday-Friday only: Saturday and public-holiday delivery are out of scope
// (docs/requirements.md Out of scope) — mirrors apps/api's Weekday enum.
export const WEEKDAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export type DeliveryGroup = {
  id: string;
  name: string;
  weekdays: Weekday[];
  defaultTruckId: string | null;
  addressCount: number;
};

export type CreateDeliveryGroupRequest = {
  name: string;
  weekdays: Weekday[];
  defaultTruckId?: string | undefined;
};
export type UpdateDeliveryGroupRequest = CreateDeliveryGroupRequest;

export type Truck = {
  id: string;
  name: string;
  bottleCapacity: number;
};

export type CreateTruckRequest = {
  name: string;
  bottleCapacity: number;
};
export type UpdateTruckRequest = CreateTruckRequest;

export type AssignDeliveryGroupRequest = {
  deliveryGroupId: string;
  priority?: number | undefined;
};

// REQ-4: `GET .../next-delivery-date`. `nextDeliveryDate` is an ISO `LocalDate` ("2026-10-05",
// no time or zone — apps/api computes it in Europe/Madrid) or `null` when the address has no
// delivery group yet.
export type NextDeliveryDateResponse = {
  nextDeliveryDate: string | null;
};

// REQ-3/REQ-4: `GET /delivery-groups/{id}/next-delivery-date`, called with a group that has just
// been picked inline for an address that has none yet — before that address is assigned to it
// (assigning the group and creating the order both still happen together on submit). Unlike the
// address-scoped response above, a group always has at least one weekday, so this field is never
// null.
export type GroupNextDeliveryDateResponse = {
  nextDeliveryDate: string;
};

/* ********************* */
/* WEEKLY GROUP MESSAGE  */
/* ********************* */

// REQ-11/REQ-35 (apps/api's WeeklyMessageMode, docs/class.puml): off sends nothing, ghost records
// who would be messaged and sends nothing, live sends the WhatsApp template to opted-in customers.
export enum WeeklyMessageMode {
  OFF = 'OFF',
  GHOST = 'GHOST',
  LIVE = 'LIVE'
}

// REQ-13 (apps/api's WeeklyMessageOutcome): WOULD_SEND only occurs in a GHOST send, SENT and
// FAILED only in a LIVE one -- there is no opted-out outcome (a customer who never opted in is
// never a recipient at all).
export enum WeeklyMessageOutcome {
  WOULD_SEND = 'WOULD_SEND',
  SENT = 'SENT',
  FAILED = 'FAILED'
}

// REQ-13: one customer a weekly-message run considered, by id -- the UI resolves the name itself
// from the existing customer endpoints (apps/ui/README.md § Weekly Group Message).
export type WeeklyMessageRecipient = {
  customerId: string;
  templateLanguage: string;
  outcome: WeeklyMessageOutcome;
};

// REQ-13: one delivery group's weekly-message run (`GET
// /delivery-groups/{id}/weekly-message-sends`). `sentCount`/`failureCount` are the API's own
// derived counts, never recomputed here (apps/api's WeeklyMessageSendResponse Javadoc):
// `sentCount` is customers sent (LIVE) or would-send (GHOST), `failureCount` is LIVE failures only.
export type WeeklyMessageSend = {
  id: string;
  deliveryGroupId: string;
  weekStart: string;
  mode: WeeklyMessageMode;
  templateName: string;
  sentAt: string;
  sentCount: number;
  failureCount: number;
  recipients: WeeklyMessageRecipient[];
};

// The standard page envelope every paginated collection GET returns
// (spring-boot/RULES.md § Pagination).
export type Page<T> = {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
};

/* **************** */
/* DASHBOARD        */
/* **************** */

// REQ-8 (owner decision 2026-10-24, SHARED_CONTRACTS round 2 "Dashboard totals"): one month's
// count for a Dashboard chart -- `month` is 1-based (1=January...12=December, `java.time.Month`'s
// own convention, apps/api's MonthlyCount.java), not dayjs's 0-based one -- callers must subtract
// one before indexing a month-name array (src/utils/kpiCalculations.ts does this once, at ingest).
export type MonthlyCount = { year: number; month: number; count: number };

// GET /api/v1/dashboard/totals -- a single, unpaged object of database-computed aggregates, never
// a per-record list (apps/api's DashboardTotalsResponse.java, replacing this plan's earlier
// per-record `customerCreatedAtTimestamps`/`subscriptions` shape). `activeSubscriptionsByType` and
// `activeSubscriptionRevenueByType` are keyed by the full `SubscriptionType` enum; a type with no
// active subscription is absent from the map, not a zero-valued entry -- read a missing key as
// zero (src/utils/kpiCalculations.ts does, the same way it already folds E_MAX and ELEGANCE into
// "yearly"). `newSubscriptionsPerMonth` buckets every subscription, active or not, same as before.
export type DashboardTotalsResponse = {
  totalCustomers: number;
  activeCustomers: number;
  inactiveCustomers: number;
  activeSubscriptionsByType: Partial<Record<SubscriptionType, number>>;
  activeSubscriptionRevenueByType: Partial<Record<SubscriptionType, number>>;
  orderCountsByStatus: Partial<Record<OrderStatus, number>>;
  newCustomersPerMonth: MonthlyCount[];
  newSubscriptionsPerMonth: MonthlyCount[];
};

/* **************** */
/* PLANNING INPUTS  */
/* **************** */

// REQ-5: the route generator's editable settings (a singleton row, seeded with defaults by
// apps/api — see PlanningSettingsSeeder.java). Time fields are `LocalTime`, serialized
// "HH:mm:ss" (apps/api/README.md § Route Planning).
type DepotAddress = {
  street: string;
  city: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
};

export type PlanningSettings = {
  cutoffTime: string;
  depotAddress: DepotAddress;
  workingDayStart: string;
  softEnd: string;
  latestFinish: string;
  baseMinutesPerStop: number;
  extraMinutesPerBottle: number;
  // REQ-5: the average speed (km/h) used for straight-line travel time estimates, > 0
  // (apps/api/README.md § Route Planning). `UpdatePlanningSettingsRequest.averageSpeedKmh` is
  // nullable/optional on the API so an older caller can omit it and keep the stored value — this
  // app always re-sends every field it read, so it stays required here.
  averageSpeedKmh: number;
  // REQ-11/REQ-35: the weekly group message's own send time and mode, nullable/optional on the API
  // the same way averageSpeedKmh is (an older caller may omit them) — this app always re-sends both.
  weeklyMessageTime: string;
  weeklyMessageMode: WeeklyMessageMode;
};

// Same shape as PlanningSettings, but every field required on write — `UpdatePlanningSettingsRequest`
// (apps/api) — except the depot's own lat/lng, which stay optional (the seeded depot has none).
export type UpdatePlanningSettingsRequest = {
  cutoffTime: string;
  depotAddress: {
    street: string;
    city: string;
    postalCode: string;
    latitude?: number | undefined;
    longitude?: number | undefined;
  };
  workingDayStart: string;
  softEnd: string;
  latestFinish: string;
  baseMinutesPerStop: number;
  extraMinutesPerBottle: number;
  averageSpeedKmh: number;
  weeklyMessageTime: string;
  weeklyMessageMode: WeeklyMessageMode;
};

// REQ-17: a driver, with an optional default truck (a time reservation names a driver, not a
// truck — apps/api/README.md § Route Planning).
export type Driver = {
  id: string;
  name: string;
  defaultTruckId: string | null;
};

export type CreateDriverRequest = {
  name: string;
  defaultTruckId: string | null;
};
export type UpdateDriverRequest = CreateDriverRequest;

// REQ-7: one Loyverse catalogue item and its bottles-per-unit value. The API lists every item;
// `bottleValue: null` means none has been set yet, the unset flag, never zero.
export type ItemBottleValue = {
  itemId: string;
  itemName: string | null;
  bottleValue: number | null;
};

export type SetItemBottleValueRequest = {
  bottleValue: number;
};

// REQ-7: one Loyverse catalogue item and whether the ordering agent may order it -- the API lists
// every live item, `orderable: true` exactly when staff marked it (apps/api's
// AgentOrderableItemResponse.java).
export type AgentOrderableItem = {
  itemId: string;
  itemName: string;
  orderable: boolean;
};

/* **************** */
/* AGENT LOG        */
/* **************** */

// REQ-1/REQ-2 (docs/agent-turn-state.puml): one turn is the agent's handling of one incoming
// Chatwoot message. `decision` and `reply` are set only once a COMPLETED turn reaches one;
// `error` only once a turn FAILED (apps/api's AgentTurnResponse).
export type AgentMode = 'OFF' | 'SHADOW' | 'LIVE';
type AgentTurnStatus = 'COMPLETED' | 'FAILED' | 'RETRIED';
export type AgentDecision =
  'ORDER_CREATED' | 'SHADOW_ORDER' | 'ESCALATION' | 'FAILED_CUSTOMER_MATCH' | 'REFUSAL';

// REQ-25 (docs/agent-conversation-state.puml): where a Chatwoot conversation stands, as the agent
// records it after every turn (apps/api's AgentConversationResponse.AgentConversationStatus).
export type AgentConversationStatus =
  | 'COLLECTING_ITEMS'
  | 'CHOOSING_ADDRESS'
  | 'AWAITING_CONFIRMATION'
  | 'ORDER_PLACED'
  | 'HANDED_OVER';

// REQ-25: one line of a conversation's order draft (apps/api's
// AgentConversationResponse.DraftLineResponse). `itemId` is null exactly when `water` is true, the
// generic water choice; the response carries no item name, so the Agent log resolves it from the
// catalogue (utils/agent-conversation.ts).
export type AgentConversationDraftLine = {
  id: string;
  itemId: string | null;
  water: boolean;
  quantity: number;
};

// REQ-25: a conversation's state as it is NOW, not as it was at the turn that carries it
// (apps/api's AgentConversationResponse). `customerId`/`customerName` are null while no customer is
// identified and after the customer is deleted. `draftDeliveryAddressNumber` is the draft address's
// number in the customer's address list (the numbering the agent's `{{ addresses }}` uses), null
// while none is chosen or the chosen one is no longer theirs. `continuedFrom` is the Chatwoot
// conversation this state was taken over from.
export type AgentConversation = {
  id: string;
  conversationId: number;
  customerId: string | null;
  customerName: string | null;
  status: AgentConversationStatus;
  draftLines: AgentConversationDraftLine[];
  draftDeliveryAddressId: string | null;
  draftDeliveryAddressNumber: number | null;
  continuedFrom: number | null;
  version: number;
  createdAt: string;
  updatedAt: string;
};

export type AgentTurn = {
  id: string;
  conversationId: number;
  messageId: number;
  mode: AgentMode;
  status: AgentTurnStatus;
  // Raw JSON text, call order (apps/api's AgentTurnResponse) -- parsed for display by
  // utils/agent-turn-tool-calls.ts, never trusted to a fixed shape here: the agent runtime that
  // writes a real one ships in M3.
  toolCalls: string;
  decision: AgentDecision | null;
  reason: string | null;
  reply: string | null;
  error: string | null;
  orderId: string | null;
  createdAt: string;
  // REQ-25: the turn's conversation as it stands now, the same object on every turn of one
  // conversation; null for a turn recorded before the state existed and for a conversation the
  // agent never reached (mode off).
  conversation: AgentConversation | null;
};

// Every filter `GET /agent-turns` takes, each independently optional (apps/api/README.md § Agent
// Log). `conversationId`/`orderId` are set by navigation (an order's own "view its turns" link,
// REQ-2, and a turn's own "view this conversation" action) rather than typed in by staff;
// `decision`/`from`/`to` are the Agent log page's own filter bar.
export type AgentTurnFilters = {
  conversationId?: number;
  orderId?: string;
  decision?: AgentDecision;
  from?: string;
  to?: string;
};

// REQ-6/REQ-16: the agent's own settings, a singleton (apps/api's AgentSettingsResponse and
// UpdateAgentSettingsRequest). `model` is the OpenRouter model id, free text, null until staff set
// one. The PUT is a full replace: the API's own `model` is optional and omitting it clears it, so
// this app always sends it, the current value when only the mode changed.
// REQ-31: `renewalNotices` is the text for a customer whose renewal is due, one per language, staff
// edited; the API always holds one for every language it supports (docs/class.puml), so every
// CustomerLanguage is present in a response.
export type AgentSettings = {
  mode: AgentMode;
  model: string | null;
  renewalNotices: Record<CustomerLanguage, string>;
};

// REQ-31: unlike `mode` and `model`, `renewalNotices` is not a full replace -- omitted changes no
// text, each language given replaces its text, and a language left out keeps its own, so a save
// sends only the texts staff edited and a write of another setting never overwrites one.
export type UpdateAgentSettingsRequest = {
  mode: AgentMode;
  model: string | null;
  renewalNotices?: Partial<Record<CustomerLanguage, string>> | undefined;
};

/* **************** */
/* KNOWLEDGE BASE   */
/* **************** */

// REQ-26/REQ-27: one entry staff keep for the agent (apps/api's KnowledgeBaseEntryResponse, every
// field of the aggregate in docs/class.puml). `position` is the order staff set, the one the agent
// gets the enabled entries in; `version` is the one an update must send back. Only the text of an
// enabled entry goes to the AI provider, never the title; neither is ever null.
export type KnowledgeBaseEntry = {
  id: string;
  title: string;
  text: string;
  enabled: boolean;
  position: number;
  version: number;
};

// REQ-26/REQ-27: one page of the knowledge base (apps/api's KnowledgeBasePageResponse): the
// standard page envelope plus how much of the limit all the enabled entries use, which a page of
// entries cannot show. `enabledCharacters` and `charactersLeft` cover the whole base, not just the
// page; the limits are sent so no client repeats them, and `maxEntries` is how many entries the
// base may hold (a new entry is refused at that many). The list is always in position order.
export type KnowledgeBasePage = Page<KnowledgeBaseEntry> & {
  enabledCharacters: number;
  charactersLeft: number;
  maxEntryCharacters: number;
  maxEnabledCharacters: number;
  maxEntries: number;
};

// REQ-27: the whole knowledge base this app holds at once, collected from every page of the list,
// so a reorder can name every entry (hooks/use-knowledge-base.ts). Holding the whole base, its
// number of entries is `entries.length`, the API's `totalElements`.
export type KnowledgeBase = {
  entries: KnowledgeBaseEntry[];
  enabledCharacters: number;
  charactersLeft: number;
  maxEntryCharacters: number;
  maxEnabledCharacters: number;
  maxEntries: number;
};

// POST /api/v1/knowledge-base-entries -- 201, body `{id}` only; the entry goes last.
export type CreateKnowledgeBaseEntryResponse = { id: string };

export type CreateKnowledgeBaseEntryRequest = {
  title: string;
  text: string;
  enabled: boolean;
};

// PUT /api/v1/knowledge-base-entries/{id} -- 204. Enabling and disabling is this same call: the
// entry as read, with `enabled` flipped, and its version. A stale `version` answers 409.
export type UpdateKnowledgeBaseEntryRequest = {
  title: string;
  text: string;
  enabled: boolean;
  version: number;
};

// PUT /api/v1/knowledge-base-entries/order -- 204. `entryIds` is every entry, enabled or not, first
// to last; a list that is not exactly the stored set answers 409.
export type ReorderKnowledgeBaseRequest = {
  entryIds: string[];
};

/* **************** */
/* AGENT ORDERS     */
/* **************** */

// REQ-9: one row of the review queue -- the order itself plus the conversation it came from
// (apps/api's AgentOrderReviewResponse). `conversationId`/`conversationUrl` are null only in the
// defensive case of an agent order with no matching AgentTurn row (should not occur in practice).
export type AgentOrderReview = {
  order: Order;
  conversationId: string | null;
  conversationUrl: string | null;
};

/* **************** */
/* DATA CLEANSING   */
/* **************** */

// REQ-33: one check the API offers (apps/api's DataCleansingCheckResponse). `key` is the stable
// slug in the rows path; `name` is the display text the API owns, so the page holds no per-check
// text; `count` is how many rows the check lists right now (its rows' `totalElements`).
export type DataCleansingCheck = {
  key: string;
  name: string;
  count: number;
};

// REQ-20/REQ-33: one record a check flags (apps/api's DataCleansingRowResponse), the same shape
// for every check. `customerId` is the customer page to open, at `addressId` when it is not null
// (an address check). `group` is the shared phone or email the shared check grouped the row under,
// null for a check that does not group. The phone is as stored: `phoneCountry` is malformed on
// purpose in the E.164 check (for example +1-2), so it is never rebuilt into a phone.
export type DataCleansingRow = {
  customerId: string;
  firstName: string;
  lastName: string | null;
  phoneCountry: string;
  phoneNumber: string;
  email: string | null;
  addressId: string | null;
  group: string | null;
};

/* **************** */
/* ROUTE PLANNING   */
/* **************** */

// REQ-8/REQ-13: a stop's kind — a customer delivery, a depot reload, or a driver break (owner
// decision 2026-10-05, docs/session-handoff.md "M4 route timing and ride breaks": a break is a
// driver's own break, never a reload).
export type RouteStopType = 'DELIVERY' | 'RELOAD' | 'BREAK';

// REQ-8/REQ-10/REQ-38: why an order dated today is not on the route. AWAITING_REVIEW: an
// agent-created order staff have not approved yet -- wins over LATE (docs/order-state.puml
// REVIEW_MARK). UNASSIGNED_ADDRESS (M14): now means only an address in no delivery group at all --
// an address whose group simply does not deliver this weekday is NOT_A_DELIVERY_DAY instead.
// Staff can pull in an order excluded as LATE or NOT_A_DELIVERY_DAY, no other reason ("Add to this
// day").
export type ExcludedOrderReason =
  | 'LATE'
  | 'UNASSIGNED_ADDRESS'
  | 'NOT_A_DELIVERY_DAY'
  | 'NO_COORDINATES'
  | 'BOTTLE_VALUE_MISSING'
  | 'NO_ROOM'
  | 'AWAITING_REVIEW';

// REQ-15: one product line on a stop or an excluded order. `bottles: null` means the item has no
// stored bottle value yet (REQ-7: flagged, never counted as zero).
export type RouteProduct = {
  itemName: string;
  quantity: number;
  bottles: number | null;
};

// One ordered stop on a truck's route. Delivery-only fields (`orderId`, `customerName`,
// `products`, `totalBottles`, `note`) are non-null iff `type === 'DELIVERY'` (`note` can still be
// null there -- an order with none); break-only fields (`label`, `minutes`) are non-null iff
// `type === 'BREAK'`. `manuallyIncludedBy`/`manuallyIncludedAt` are set only for a delivery stop
// staff pulled in, late (REQ-8) or on a day its group does not deliver (REQ-38). `extraStop`
// (REQ-38) is true for a delivery whose order's address group does not deliver this weekday,
// whether it got on through the order's own "deliver anyway" flag or a staff pull-in; false for
// every other stop.
export type RouteStop = {
  sequenceNumber: number;
  type: RouteStopType;
  orderId: string | null;
  estimatedArrival: string;
  manuallyIncludedBy: string | null;
  manuallyIncludedAt: string | null;
  extraStop: boolean;
  label: string | null;
  minutes: number | null;
  customerName: string | null;
  products: RouteProduct[] | null;
  totalBottles: number | null;
  note: string | null;
};

// REQ-10/REQ-12/REQ-14: one truck's ordered route for the day. `truckName`/`driverName` are read
// live by the API (never stored on the route itself), so the view shows them straight from this
// response instead of looking them up from the paged trucks/drivers lists (capped at 100).
// `driverId`/`driverName`/`reservationLabel` are null when no driver ended up assigned (every
// available driver was busy or reserved for the whole day). `truckName`, and `driverName` with a
// non-null `driverId`, are null when that truck or driver was deleted after the route was generated.
// `softEndOverrunMinutes` is non-null only when the truck finishes after the soft end (REQ-11: a
// warning, not an exclusion).
export type TruckRoute = {
  truckId: string;
  truckName: string | null;
  driverId: string | null;
  driverName: string | null;
  estimatedFinishTime: string;
  softEndOverrunMinutes: number | null;
  reservationLabel: string | null;
  reservationMinutes: number | null;
  stops: RouteStop[];
};

// REQ-8: an order dated this day that did not make the route, and why.
export type ExcludedOrder = {
  orderId: string;
  reason: ExcludedOrderReason;
  customerName: string | null;
  products: RouteProduct[] | null;
  totalBottles: number | null;
};

// REQ-13: a delivery day's stored route. `GET /api/v1/routes/{date}` 404s when nothing has been
// generated yet for that date — useRoute (src/hooks/use-route.ts) treats that as `null`, an empty
// state, not an error (react-base/RULES.md § Query-state rendering).
export type Route = {
  deliveryDate: string;
  generatedAt: string;
  optimized: boolean;
  notice: string | null;
  truckRoutes: TruckRoute[];
  excludedOrders: ExcludedOrder[];
};

// REQ-12: a driver time reservation given when generating a day, such as "machine cleaning".
type DriverReservationRequest = {
  driverId: string;
  reservedMinutes: number;
  label: string;
};

// A driver's own break (owner decision 2026-10-05 — docs/session-handoff.md "M4 route timing and
// ride breaks"): never added by default, staff add one per driver when generating.
// `earliestStart` is a bare `LocalTime` ("HH:mm:ss", react-base/RULES.md § Dates & time's
// wall-clock exception) or null — placed at the first stop boundary at or after that time, or
// after the middle of that driver's deliveries when absent.
type DriverBreakRequest = {
  driverId: string;
  label: string;
  minutes: number;
  earliestStart: string | null;
};

export type GenerateRouteRequest = {
  reservations: DriverReservationRequest[];
  breaks: DriverBreakRequest[];
};

/* **************** */
/* RECEIPT INBOX    */
/* **************** */

// REQ-4/REQ-5 (docs/receipt-inbox-state.puml): a Loyverse receipt stored first and processed
// later, exactly once, by a scheduled worker. This app only ever lists the two statuses that wait
// for a staff decision — NEEDS_ATTENTION and WAITING_FOR_CONFIRMATION (apps/ui/README.md §
// Receipt Review) — the type still carries every status so an item read back after a decision
// (e.g. REJECTED) type-checks.
export type ReceiptInboxStatus =
  | 'RECEIVED'
  | 'PROCESSED'
  | 'NEEDS_ATTENTION'
  | 'WAITING_FOR_CONFIRMATION'
  | 'REJECTED'
  | 'IGNORED_DUPLICATE';

// The kind of subscription change one line causes (apps/api's
// ReceiptInboxLineResponse.ChangeKind) — this, not the raw Loyverse itemId, is "the planned
// change" staff see.
export type ReceiptInboxChangeKind = 'CREATE' | 'RENEW' | 'CANCEL';

type ReceiptInboxLine = {
  itemId: string;
  // Known only for a version received through the webhook; a version the backfill runner
  // synthesized from a pre-inbox row carries none (apps/api's ReceiptInboxLineResponse).
  itemName: string | null;
  quantity: number;
  price: number;
  total: number;
  changeKind: ReceiptInboxChangeKind;
};

// One of the linked customer's live delivery addresses, to pick from (REQ-5).
export type ReceiptInboxAddress = {
  id: string;
  street: string;
  city: string;
  postalCode: string;
};

// Returned by both the list and the detail endpoint on apps/api (ReceiptInboxResponse) — a small,
// low-volume admin queue, one shape for both.
export type ReceiptInbox = {
  id: string;
  loyverseReceiptNumber: string;
  receiptCreatedAt: string;
  // REQ-5: null is the "unknown customer" flag — staff link one before any decision.
  customerId: string | null;
  // The linked customer's display name, so staff never have to read customerId's raw UUID. Null
  // exactly when customerId is null.
  customerName: string | null;
  // Set by matching or picked by staff; null until then (REQ-4, REQ-5).
  deliveryAddressId: string | null;
  // REQ-5: a refund is never confirmable and changes no subscription.
  refund: boolean;
  status: ReceiptInboxStatus;
  // REQ-6: the "changed" mark — a later delivery with different content than the one already
  // applied. Milestone 3 builds its own badge for it; this type just carries it.
  changed: boolean;
  rejectionReason: string | null;
  versionsReceivedAt: string[];
  // The order already holding this receipt's number, if one does.
  orderId: string | null;
  customerAddresses: ReceiptInboxAddress[];
  lines: ReceiptInboxLine[];
};

// The standard page envelope plus `needsAttentionCount` (REQ-5): the whole inbox's own
// NEEDS_ATTENTION count, independent of whichever status this particular page was read with
// (apps/api's ReceiptInboxPageResponse).
export type ReceiptInboxPageResponse = Page<ReceiptInbox> & {
  needsAttentionCount: number;
};

export type LinkReceiptInboxCustomerRequest = {
  customerId: string;
};

// REQ-5: picks one of a known customer's several delivery addresses for an item that needs
// attention; applies nothing itself — apps/api returns the item to RECEIVED for the worker's next
// tick (docs/receipt-inbox-state.puml RI8).
export type PickReceiptInboxAddressRequest = {
  deliveryAddressId: string;
};

export type RejectReceiptInboxRequest = {
  reason: string;
};

/* ******** */
/* PAYMENTS */
/* ******** */

// REQ-40/REQ-44: the editable list staff pick a method from (apps/api's PaymentMethodResponse). A
// deactivated method is not offered for a new payment but stays on the payments that used it.
export type PaymentMethod = {
  id: string;
  name: string;
  active: boolean;
  position: number;
  version: number;
};

export type CreatePaymentMethodRequest = {
  name: string;
};

export type UpdatePaymentMethodRequest = {
  name: string;
  active: boolean;
  version: number;
};

// REQ-40: one part of a payment that pays one order (apps/api's PaymentResponse.AllocationResponse).
export type PaymentAllocation = {
  orderId: string;
  amount: number;
};

// REQ-40: one payment as staff see it (apps/api's PaymentResponse) -- `paymentMethodName` is the
// method's current name, deactivated or not; `loyverseReceiptNumber` is set only for a final
// receipt the receipt worker recorded (REQ-42), never by a staff payment.
export type Payment = {
  id: string;
  customerId: string;
  paymentDate: string;
  amount: number;
  paymentMethodId: string;
  paymentMethodName: string;
  reference: string | null;
  recordedBy: string;
  loyverseReceiptNumber: string | null;
  createdAt: string;
  allocations: PaymentAllocation[];
  version: number;
};

// REQ-40: staff record a payment, split in full over the customer's owing orders, never more than
// an order owes (apps/api's RecordPaymentRequest). `reference` is optional staff text (a transfer
// reference, a receipt number typed by hand).
export type RecordPaymentRequest = {
  customerId: string;
  paymentDate: string;
  amount: number;
  paymentMethodId: string;
  reference?: string | undefined;
  allocations: { orderId: string; amount: number }[];
};

/* ****** */
/* ERRORS */
/* ****** */
// REQ-14: every error apps/api's GlobalExceptionHandler answers is this shape
// (application/problem+json, Spring's ProblemDetail) — `type` is always the literal "about:blank".
// `detail` is absent only on the unauthenticated 404 AuthenticationFilter hand-builds (no/expired
// Firebase token, e.g. a refresh that failed) — a shape the UI can reach, so it is never treated
// as a parse failure, just a missing field. `fields` (field name to message) is present only on a
// 400 from bean validation.
export type ProblemDetail = {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance: string;
  fields?: Record<string, string>;
};
